/**
 * HomepageSmartSearch — Perplexity-style cited-answer search box on the
 * Landing hero. Now with:
 *   - Smart autocomplete (GET /api/smart-search/autocomplete)
 *   - Semantic intent (backend enriches every query with Central Rentals
 *     Canada brand context + excludes competitor brands like Web Realty)
 *   - Automated synonyms (server-side; no zero-result pages)
 *   - Conversational follow-ups (client keeps last 3 turns and posts them
 *     with the next question so context is preserved)
 *
 * Renders nothing if no provider key is configured server-side.
 */
import { useEffect, useRef, useState } from "react";
import { Search, Loader2, ExternalLink, Sparkles, MessageCircle, X } from "lucide-react";
import { useT } from "@/lib/i18n";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const MAX_HISTORY = 3;

function CitationChip({ source }) {
  if (!source?.url) return null;
  let host = "";
  try { host = new URL(source.url).hostname.replace(/^www\./, ""); } catch { /* noop */ }
  return (
    <a
      href={source.url}
      target="_blank"
      rel="noopener noreferrer nofollow"
      data-testid={`smart-search-cite-${source.index}`}
      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white border border-slate-200 hover:border-emerald-400 hover:bg-emerald-50 text-xs transition-colors max-w-[200px]"
      title={source.title || source.url}
    >
      <span className="font-bold text-emerald-700 shrink-0">[{source.index}]</span>
      <span className="truncate text-slate-700">{host || source.title || "source"}</span>
      <ExternalLink className="w-3 h-3 text-slate-400 shrink-0"/>
    </a>
  );
}

export default function HomepageSmartSearch() {
  const t = useT();
  const [ready, setReady] = useState(null); // null = unknown, false = hide
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [answer, setAnswer] = useState("");
  const [sources, setSources] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [history, setHistory] = useState([]); // [{q, a}, ...] – client-side rolling context
  const debounceRef = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/api/smart-search/status`)
      .then((r) => r.json())
      .then((d) => setReady(!!d.ready))
      .catch(() => setReady(false));
  }, []);

  // Debounced autocomplete — 220ms after the user stops typing.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) { setSuggestions([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/api/smart-search/autocomplete?q=${encodeURIComponent(q)}&limit=6`);
        const d = await r.json();
        setSuggestions(Array.isArray(d.suggestions) ? d.suggestions : []);
      } catch { /* silent — autocomplete is best-effort */ }
    }, 220);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query]);

  // Click-outside closes the suggestions dropdown.
  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setShowSuggest(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  async function submitQuery(q) {
    if (q.length < 3) return;
    setBusy(true); setError(""); setAnswer(""); setSources([]); setShowSuggest(false);
    try {
      const r = await fetch(`${API}/api/smart-search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: q, history: history.slice(-MAX_HISTORY) }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || "Search failed");
      const a = d.answer || "";
      setAnswer(a);
      setSources(d.sources || []);
      setHistory((prev) => [...prev.slice(-(MAX_HISTORY - 1)), { q, a }]);
    } catch (err) {
      setError(String(err.message || err));
    } finally {
      setBusy(false);
    }
  }

  async function ask(e) {
    e?.preventDefault?.();
    await submitQuery(query.trim());
  }

  function pickSuggestion(s) {
    setQuery(s);
    setShowSuggest(false);
    // Fire immediately — a suggestion click is a decisive action.
    submitQuery(s);
  }

  function clearConversation() {
    setHistory([]); setAnswer(""); setSources([]); setQuery(""); setError("");
  }

  // Hide entirely until status check resolves to true.
  if (ready !== true) return null;

  const canFollowUp = history.length > 0;

  return (
    <div className="w-full max-w-3xl mx-auto mt-10" data-testid="homepage-smart-search" ref={boxRef}>
      <form onSubmit={ask} className="relative">
        <div className="flex items-center gap-2 bg-white border-2 border-slate-200 focus-within:border-emerald-500 rounded-2xl shadow-sm px-4 py-3 transition-colors">
          <Sparkles className="w-5 h-5 text-emerald-600 shrink-0"/>
          <input
            value={query}
            onChange={(e) => { setQuery(e.target.value); setShowSuggest(true); }}
            onFocus={() => setShowSuggest(true)}
            placeholder={
              canFollowUp
                ? t("landing.homepage_search_followup_ph", "Ask a follow-up… (e.g. What about Alberta?)")
                : t("landing.homepage_search_ph", "Ask about Canadian rental law, tenant rights, eviction rules…")
            }
            data-testid="smart-search-input"
            className="flex-1 bg-transparent text-sm md:text-base text-slate-900 placeholder:text-slate-400 outline-none"
            maxLength={400}
            autoComplete="off"
          />
          {canFollowUp && (
            <button
              type="button"
              onClick={clearConversation}
              title="Start a new conversation"
              className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-colors text-xs"
              data-testid="smart-search-clear"
            >
              <X className="w-3.5 h-3.5"/>
              <span className="hidden sm:inline">Clear</span>
            </button>
          )}
          <button
            type="submit"
            disabled={busy || query.trim().length < 3}
            data-testid="smart-search-submit"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Search className="w-4 h-4"/>}
            <span className="hidden sm:inline">{busy ? "Searching" : canFollowUp ? "Ask" : "Ask"}</span>
          </button>
        </div>

        {showSuggest && suggestions.length > 0 && (
          <div
            data-testid="smart-search-suggestions"
            className="absolute left-0 right-0 top-full mt-2 z-30 bg-white border border-slate-200 rounded-2xl shadow-xl overflow-hidden"
          >
            <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-widest font-bold text-slate-500">
              Try one of these
            </div>
            <ul className="divide-y divide-slate-100">
              {suggestions.map((s, i) => (
                <li key={s}>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault() /* keep focus on input */}
                    onClick={() => pickSuggestion(s)}
                    className="w-full text-left flex items-center gap-3 px-4 py-2.5 hover:bg-emerald-50 text-sm text-slate-800 transition-colors"
                    data-testid={`smart-search-suggest-${i}`}
                  >
                    <Search className="w-3.5 h-3.5 text-slate-400 shrink-0"/>
                    <span className="truncate">{s}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </form>

      {canFollowUp && (
        <div
          data-testid="smart-search-history"
          className="mt-3 flex items-center gap-2 text-xs text-slate-500"
        >
          <MessageCircle className="w-3.5 h-3.5 text-emerald-600"/>
          <span>Conversation open — follow-ups keep the previous {history.length}-turn context.</span>
        </div>
      )}

      {error && (
        <div data-testid="smart-search-error" className="mt-3 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
          {error}
        </div>
      )}

      {answer && (
        <div
          data-testid="smart-search-answer"
          className="mt-4 bg-white border border-slate-200 rounded-2xl p-5 shadow-sm"
        >
          <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 mb-2">Answer</div>
          <p className="text-sm md:text-base text-slate-800 leading-relaxed whitespace-pre-line">{answer}</p>
          {sources.length > 0 && (
            <>
              <div className="mt-5 mb-2 text-[10px] uppercase tracking-widest font-bold text-slate-500">Sources</div>
              <div className="flex flex-wrap gap-2">
                {sources.map((s) => <CitationChip key={s.index} source={s}/>)}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
